import {
    CreditCardOutlined,
    DeviceThermostatOutlined,
    GroupOutlined,
    MapsHomeWork,
    StarOutline,
    VpnKeyOutlined,
    WaterDropOutlined,
    WorkspacePremium,
} from "@mui/icons-material";
import {
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { InsightsPropertyTree } from "graphql-types/graphql";
import {
    AssetSalesPrice,
    ComingSoonBadge,
    EPCClassification,
    Title,
} from "legacyNext/components";
import { useAssetOwnershipQuery } from "legacyNext/hooks/useAssetOwnership";
import { useAssetWaterSupplierQuery } from "legacyNext/hooks/useAssetWaterSupplier";
import { useHeatInstallationQuery } from "legacyNext/hooks/useHeatInstallation";

type Props = {
    area: number | null;
    assetExternalId: string;
    building: InsightsPropertyTree;
};

export const AssetProfile = (props: Props) => {
    const { assetExternalId, area, building } = props;

    const { t } = useTranslation();
    const { data: assetOwnershipData, loading: assetOwnershipLoading } =
        useAssetOwnershipQuery(assetExternalId);
    const { data: heatInstallationData } =
        useHeatInstallationQuery(assetExternalId);
    const { data: waterSupplierData } =
        useAssetWaterSupplierQuery(assetExternalId);
    const waterSupplier = waterSupplierData?.waterSupplier
        ? waterSupplierData?.waterSupplier
        : "-";
    if (!building) {
        return null;
    }

    return (
        <>
            <List>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <MapsHomeWork fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <Typography variant="h5" fontWeight={600}>
                            {t("legacyNext.sqm", "sqm", {
                                value: area,
                            })}
                        </Typography>
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.area", "area")}
                        </Typography>
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <WorkspacePremium fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <Typography variant="h5" fontWeight={600}>
                            {t("legacyNext.energyLabels", "energy labels")}
                        </Typography>
                        <EPCClassification assetExternalId={assetExternalId} />
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                            <Typography>
                                {building.objectType[0].toUpperCase()}
                            </Typography>
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText>
                        <Typography variant="h5" fontWeight={600}>
                            {assetExternalId}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            BFE
                        </Typography>
                    </ListItemText>
                </ListItem>
            </List>
            <Divider orientation="vertical" flexItem />
            <List>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <CreditCardOutlined fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <AssetSalesPrice assetExternalId={assetExternalId} />
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.salePrice", "sale price")}
                        </Typography>
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <GroupOutlined fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <Title
                            loading={assetOwnershipLoading}
                            text={assetOwnershipData?.administratorName}
                        />
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.administrator", "administrator")}
                        </Typography>
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <VpnKeyOutlined fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <Title
                            loading={assetOwnershipLoading}
                            text={assetOwnershipData?.ownerName}
                        />
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.owner", "owner")}
                        </Typography>
                    </ListItemText>
                </ListItem>
            </List>
            <Divider orientation="vertical" flexItem />
            <List>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <DeviceThermostatOutlined fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        {heatInstallationData?.heatInstallation
                            ? heatInstallationData.heatInstallation
                            : "-"}
                        <Typography variant="h5" marginTop={1.5}>
                            {t(
                                "legacyNext.heatingInstallation",
                                "heating installation"
                            )}
                        </Typography>
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <WaterDropOutlined fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        {waterSupplier}
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.waterSupply", "water supply")}
                        </Typography>
                    </ListItemText>
                </ListItem>
                <ListItem alignItems="flex-start">
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <StarOutline fontSize="small" />
                    </ListItemAvatar>
                    <ListItemText>
                        <ComingSoonBadge />
                        <Typography variant="h5" marginTop={1.5}>
                            {t("legacyNext.certifications", "certifications")}
                        </Typography>
                    </ListItemText>
                </ListItem>
            </List>
        </>
    );
};
